import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import LandingPage from "@/components/LandingPage";

// Route-level code splitting: only the landing page is in the initial
// bundle. Every other page is fetched on demand the first time the user
// hits its URL. This typically cuts initial JS by 50-65% on the home route.
const ThankYouPage      = lazy(() => import("@/components/ThankYouPage"));
const AffiliatePage     = lazy(() => import("@/components/AffiliatePage"));
const RedirectPage      = lazy(() => import("@/components/RedirectPage"));
const GiftPage          = lazy(() => import("@/components/GiftPage"));
const GiftClaimPage     = lazy(() => import("@/components/GiftClaimPage"));
const GiftDashboardPage = lazy(() => import("@/components/GiftDashboardPage"));
const GiftThankYouPage  = lazy(() => import("@/components/GiftThankYouPage"));
const AdminGiftsPage    = lazy(() => import("@/components/AdminGiftsPage"));
const AdminLayout         = lazy(() => import("@/components/admin/AdminLayout"));
const AdminOverviewPage   = lazy(() => import("@/components/admin/AdminOverviewPage"));
const AdminOrdersPage     = lazy(() => import("@/components/admin/AdminOrdersPage"));
const AdminLeadsPage      = lazy(() => import("@/components/admin/AdminLeadsPage"));
const AdminAffiliatesPage = lazy(() =>
  import("@/components/admin/AdminPlaceholders").then((m) => ({ default: m.AdminAffiliatesPage }))
);
const AdminBroadcastsPage = lazy(() => import("@/components/admin/AdminBroadcastsPage"));
const AdminMarketingPage  = lazy(() => import("@/components/admin/AdminMarketingPage"));

// Tiny fallback so Suspense doesn't flash a blank page on slow networks.
const RouteFallback = () => (
  <div
    style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#fff8f3",
    }}
    aria-busy="true"
    aria-live="polite"
  >
    <span style={{ color: "#464646", opacity: 0.5, fontSize: 13, letterSpacing: "0.2em" }}>
      LOADING
    </span>
  </div>
);

function App() {
  useEffect(() => {
    // Defer Lenis smooth-scroll init to an idle frame so it doesn't block
    // the main thread during initial paint (kills ~200-300ms of TBT).
    // We also bail entirely on small touch devices where native scrolling
    // feels better and Lenis's input listeners add jank.
    const isCoarse = window.matchMedia?.("(pointer: coarse)").matches;
    if (isCoarse) return;

    let lenis;
    let rafId;

    const schedule = (cb) =>
      "requestIdleCallback" in window
        ? window.requestIdleCallback(cb, { timeout: 1500 })
        : setTimeout(cb, 250);

    const cancel = (id) =>
      "cancelIdleCallback" in window ? window.cancelIdleCallback(id) : clearTimeout(id);

    const idleId = schedule(async () => {
      const { default: Lenis } = await import("lenis");
      lenis = new Lenis({ lerp: 0.07, smoothWheel: true, wheelMultiplier: 0.9 });
      const raf = (time) => {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    });

    return () => {
      cancel(idleId);
      if (rafId) cancelAnimationFrame(rafId);
      lenis?.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors theme="light" />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          {/* Alias — some admin-generated payment links point to /checkout/success. Reuse the same page so no legacy link breaks. */}
          <Route path="/checkout/success" element={<ThankYouPage />} />
          <Route path="/affiliates" element={<AffiliatePage />} />
          <Route path="/affiliate" element={<AffiliatePage />} />
          <Route path="/gift" element={<GiftPage />} />
          <Route path="/gift/thank-you" element={<GiftThankYouPage />} />
          <Route path="/gift/claim/:token" element={<GiftClaimPage />} />
          <Route path="/gift/dashboard/:token" element={<GiftDashboardPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverviewPage />} />
            <Route path="orders" element={<AdminOrdersPage />} />
            <Route path="gifts" element={<AdminGiftsPage />} />
            <Route path="gifts/:orderId" element={<AdminGiftsPage />} />
            <Route path="leads" element={<AdminLeadsPage />} />
            <Route path="affiliates" element={<AdminAffiliatesPage />} />
            <Route path="broadcasts" element={<AdminBroadcastsPage />} />
            <Route path="marketing" element={<AdminMarketingPage />} />
          </Route>
          <Route path="/r/:slug" element={<RedirectPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
